// Pairing API surface for the web app.
//
// Two-step flow (HLP-003):
//   1. Backend mints a one-time pairing token.
//   2. Web app submits the token to the helper's /v1/pair on localhost.
//   3. Helper confirms the token against the backend, persists its bearer,
//      and returns paired_at + the new bearer to the web app.

import type { ApiClient } from './client';

export interface InitiateResponse {
  device_id: string;
  pairing_token: string;
  expires_at: string;
}

export interface PairResponse {
  paired_at: string;
  device_name: string;
  bearer: string;
  csrf_token: string;
}

export async function initiatePairing(
  client: ApiClient,
  deviceName?: string,
): Promise<InitiateResponse> {
  return client.post<InitiateResponse>('/v1/pairing/helper/initiate', {
    ...(deviceName ? { device_name: deviceName } : {}),
  });
}

/**
 * Submit the pairing token to the helper running on localhost.
 *
 * `helperBaseUrl` is `http://127.0.0.1:<ephemeral port>`. In Phase 1 we
 * default to the fixed discovery port (`7831`) — once HLP-002 discovery
 * is wired end-to-end (and the web app obtains the ephemeral port via
 * /v1/discover), this defaults to the discovered URL.
 */
export async function submitToHelper(
  helperBaseUrl: string,
  pairingToken: string,
): Promise<PairResponse> {
  const res = await fetch(`${helperBaseUrl}/v1/pair`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ pairing_token: pairingToken }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`helper /v1/pair failed: ${res.status} ${text}`);
  }
  return (await res.json()) as PairResponse;
}

export async function pairFlow(client: ApiClient, helperBaseUrl: string, deviceName?: string) {
  const initiated = await initiatePairing(client, deviceName);
  const paired = await submitToHelper(helperBaseUrl, initiated.pairing_token);
  return { initiated, paired };
}
